/* eslint-env browser */

import {
	Bell,
	LogOut,
	MessageCircleQuestion,
	Settings,
	User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { LanguageSwitcher } from '@/components/shared';
import { ROUTES } from '@/constants';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
	userName?: string;
	userRole?: string;
	onLogout?: () => void;
}

function Header({
	userName = 'Admin',
	userRole = 'Admin',
	onLogout,
}: HeaderProps) {
	const { t } = useTranslation(['components', 'common']);
	const [showUserMenu, setShowUserMenu] = useState(false);
	const [showNotifications, setShowNotifications] = useState(false);

	const navigate = useNavigate();
	const userMenuRef = useRef<HTMLDivElement>(null);
	const notificationsRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
				setShowUserMenu(false);
			}
			if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
				setShowNotifications(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleLogout = (): void => {
		if (onLogout) {
			onLogout();
		} else {
			navigate(ROUTES.LOGIN);
		}
	};

	return (
		<header className='bg-white/80 backdrop-blur-lg border-b border-slate-200/50 shadow-sm sticky top-0 z-40 rounded-b-2xl mt-4 mx-6 rounded-2xl border'>
			<div className='px-6 py-3'>
				<div className='flex items-center justify-between'>
					<div className='flex items-center gap-3 flex-1 min-w-0'>
						<img
							src='/borzolini-logo.svg'
							alt={t('common:app.name')}
							className='w-9 h-9 object-contain flex-shrink-0'
						/>
						<div className='min-w-0'>
							<p className='text-sm font-bold text-slate-900 truncate'>
								{t('common:app.name')} {t('common:app.panelTitle')}
							</p>
							<p className='text-xs text-slate-500 truncate'>{t('common:app.tagline')}</p>
						</div>
					</div>

					<div className='flex items-center gap-3'>
						<LanguageSwitcher />

						<div className='w-px h-8 bg-slate-200' />

						<div className='relative' ref={notificationsRef}>
							<button
								onClick={() => setShowNotifications(!showNotifications)}
								className='relative p-2 hover:bg-slate-100 rounded-xl transition-all group'
								aria-label={t('components:header.notifications')}
							>
								<Bell
									size={20}
									className='text-slate-600 group-hover:text-slate-900 transition-colors'
								/>
							</button>

							{showNotifications && (
								<div className='absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden z-50'>
									<div className='p-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50'>
										<h3 className='font-semibold text-slate-900'>
											{t('components:header.notifications')}
										</h3>
									</div>
									<div className='p-6 text-center text-sm text-slate-500'>
										No notifications yet
									</div>
								</div>
							)}
						</div>

						<div className='relative' ref={userMenuRef}>
							<button
								onClick={() => setShowUserMenu(!showUserMenu)}
								className='flex items-center gap-3 px-3 py-2 hover:bg-slate-100 rounded-xl transition-all group'
							>
								<div className='w-9 h-9 rounded-xl bg-[#6ecefd] flex items-center justify-center text-white font-bold text-sm shadow-lg group-hover:shadow-xl transition-shadow'>
									{userName.charAt(0)}
								</div>
								<div className='text-left hidden 2xl:block'>
									<p className='text-sm font-semibold text-slate-900 leading-tight'>{userName}</p>
									<p className='text-xs text-slate-500 leading-tight capitalize'>{userRole}</p>
								</div>
							</button>

							{showUserMenu && (
								<div className='absolute right-0 mt-2 w-56 bg-white rounded-2xl border border-slate-200 shadow-xl z-50 overflow-hidden'>
									<div className='p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-slate-200'>
										<div className='flex items-center gap-3'>
											<div className='w-12 h-12 rounded-xl bg-[#6ecefd] flex items-center justify-center text-white font-bold text-lg'>
												{userName.charAt(0)}
											</div>
											<div>
												<p className='text-sm font-semibold text-slate-900'>{userName}</p>
												<p className='text-xs text-slate-500 capitalize'>{userRole}</p>
											</div>
										</div>
									</div>
									<div className='p-2 space-y-1'>
										<button
											onClick={() => navigate(ROUTES.PROFILE)}
											className='w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-xl text-slate-700 text-sm font-medium transition-colors group'
										>
											<User size={18} className='text-slate-500 group-hover:text-blue-600' />
											{t('common:navigation.profile')}
										</button>
										<button
											onClick={() => navigate(ROUTES.SETTINGS)}
											className='w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-xl text-slate-700 text-sm font-medium transition-colors group'
										>
											<Settings size={18} className='text-slate-500 group-hover:text-blue-600' />
											{t('components:header.settings')}
										</button>
										<a
											href='mailto:support@borzi.app'
											className='w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-xl text-slate-700 text-sm font-medium transition-colors group'
										>
											<MessageCircleQuestion
												size={18}
												className='text-slate-500 group-hover:text-blue-600'
											/>
											Support
										</a>
										<div className='h-px bg-slate-200 my-1' />
										<button
											onClick={handleLogout}
											className='w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 rounded-xl text-red-600 text-sm font-medium transition-colors group'
										>
											<LogOut size={18} />
											{t('components:header.logout')}
										</button>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</header>
	);
}

export { Header };
export default Header;
